import {
    authenticatedApi,
    ApiError,
    API_BASE_URL,
} from './apiUtils';

// Import getEmployeePhoto from cached implementation
import { getEmployeePhoto } from './cached/employeesApi';

export const employeesApi = {

  /**
   * ADR-138 — set the employee's hire date from one of their own employment episodes.
   *
   * Distinct from the hire date sent on `updateEmployee`: that one is a free-text date
   * gated by the admin-only EmployeeHireDate permission, while this one can only adopt
   * a date the system already recorded and is gated by EmployeeHireDateFromEpisode.
   *
   * @param {string} employeeId
   * @param {string} episodeId - must belong to that employee
   * @returns {Promise<Object>} the updated employee
   */
  setHireDateFromEpisode: async (employeeId, episodeId) => {
    try {
      return await authenticatedApi.put(
        `${API_BASE_URL}/employees/${employeeId}/hire-date/from-episode`,
        { episodeId }
      );
    } catch (error) {
      throw new ApiError(
        error.message || 'Failed to set the hire date from the selected episode',
        error.status || 500
      );
    }
  },

  // ADR-171 C1 — designate another open contract as the person's primary
  designatePrimaryContract: async (employeeId, episodeId, reason) => {
    try {
      return await authenticatedApi.put(
        `${API_BASE_URL}/employees/${employeeId}/primary-contract`,
        { episodeId, ...(reason ? { reason } : {}) }
      );
    } catch (error) {
      throw new ApiError(
        error.message || 'Failed to make this contract the primary',
        error.status || 500
      );
    }
  },

  searchAzureUsers: async (query) => {
    try {

      const queryParams = new URLSearchParams();
      if (query) queryParams.append('query', query);
      
      const queryString = queryParams.toString();
      return await authenticatedApi.get(`${API_BASE_URL}/employees/azure/search?${queryString}`);
    } catch (error) {
      throw new ApiError('Failed to search Azure users', error.status || 500);
    }
  },

  getMeAuth: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/me`);
    } catch (error) {
      throw new ApiError('Failed to fetch employee by Azure ID', error.status || 500);
    }
  },

  /**
   * Get all employees from the backend with cursor-based pagination
   * @param {Object} options - Pagination and filter options
   * @param {number} options.limit - Number of items to return (default: 10)
   * @param {string} options.cursor - The cursor value to fetch the next page
   * @param {string} options.orderBy - Field to sort by (default: "fullName")
   * @param {string} options.orderDirection - Sort direction, "asc" or "desc" (default: "asc")
   * @param {string} options.search - Text to filter employees by fullName, case insensitive
   * @param {string} options.projectIds - Comma-separated project IDs to filter employees by
   * @param {string} options.clusterId - Cluster ID to filter employees by
   * @param {string} options.organizationalStatus - Comma-separated organizational statuses to filter employees by
   * @param {string} options.positionIds - Comma-separated position IDs to filter employees by
   * @param {string} options.gradeIds - Comma-separated grade IDs to filter employees by
   * @param {string} options.reportingScope - Reporting scope filter: "direct", "hierarchy", or "all"
   * @returns {Promise<Object>} - Object containing items array and pageInfo
   */
  getAllEmployees: async (options = {}) => {
    try {
      const {
        limit = 10,
        cursor,
        orderBy = "fullName",
        orderDirection = "asc",
        search = "",
        projectIds = "",
        clusterId = "",
        organizationalStatus = "",
        positionIds = "",
        gradeIds = "",
        reportingScope = ""
      } = options;

      let url = `${API_BASE_URL}/employees?limit=${limit}`;
      if (cursor) url += `&cursor=${cursor}`;
      if (orderBy) url += `&orderBy=${orderBy}`;
      if (orderDirection) url += `&orderDirection=${orderDirection}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (projectIds) url += `&projectIds=${projectIds}`;
      if (clusterId) url += `&clusterId=${clusterId}`;
      if (organizationalStatus) url += `&organizationalStatus=${organizationalStatus}`;
      if (positionIds) url += `&positionIds=${positionIds}`;
      if (gradeIds) url += `&gradeIds=${gradeIds}`;
      if (reportingScope) url += `&reportingScope=${reportingScope}`;

      return await authenticatedApi.get(url);
    } catch (error) {
      console.error('Error fetching employees');
      throw new ApiError('Failed to fetch employees', error.status || 500);
    }
  },

  getAllEmployeesLite: async ({ limit = 20, orderBy = 'fullName', orderDirection = 'asc', search = '', cursor = null } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (limit) queryParams.append('limit', limit);
      if (orderBy) queryParams.append('orderBy', orderBy);
      if (orderDirection) queryParams.append('orderDirection', orderDirection);
      if (search) queryParams.append('search', search);
      if (cursor) queryParams.append('cursor', cursor);
      
      const queryString = queryParams.toString();
      return await authenticatedApi.get(`${API_BASE_URL}/employees/lite?${queryString}`);
    } catch (error) {
      throw new ApiError('Failed to fetch employees', error.status || 500);
    }
  },

  getEmployeeById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employees/${id}`);
    } catch (error) {
      throw new ApiError('Failed to fetch employee data', error.status || 500);
    }
  },

  // ADR-101: management info pinned to a date (ADR-099 asOfDate param).
  // Response carries directManager/budgetDirector at T plus the asOf
  // provenance block ({ asOfDate, pinnedFields, resolvedVia }).
  getEmployeeAsOf: async (id, asOfDate) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employees/${id}?asOfDate=${encodeURIComponent(asOfDate)}`);
    } catch (error) {
      throw new ApiError('Failed to fetch management info for the selected date', error.status || 500);
    }
  },

  // ADR-127: My Exec — the representative of the cluster the individual's FT
  // allocation sits in. Twin of the budget-director endpoint: bare object /
  // null without asOfDate; { myExec, asOf: { asOfDate, resolvedVia, cluster,
  // resolvedAtCluster } } with it.
  getMyExec: async (id, asOfDate) => {
    try {
      const query = asOfDate ? `?asOfDate=${encodeURIComponent(asOfDate)}` : '';
      return await authenticatedApi.get(`${API_BASE_URL}/employees/${id}/my-exec${query}`);
    } catch (error) {
      throw new ApiError('Failed to fetch My Exec', error.status || 500);
    }
  },

  // ADR-101 phase 2: who managed this individual over time — merged intervals.
  // Requires the ManagementHistory ability (403 without it).
  getManagementHistory: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employees/${id}/management-history`);
    } catch (error) {
      throw new ApiError('Failed to fetch management info history', error.status || 500);
    }
  },

  createEmployee: async (employeeData) => {
    try {
      const payload = {
        azureId: employeeData.azureId,
        email: employeeData.email,
        fullName: employeeData.fullName,
        hireDate: employeeData.hireDate,
        contractTypeId: employeeData.contractType,
        engagementModelId: employeeData.engagementModel || null,
        contractCompanyId: employeeData.contractCompany,
        workingLocationId: employeeData.workingLocation,
        clusterId: employeeData.cluster,
        gradeId: employeeData.grade,
        positionId: employeeData.position,
      };

      return await authenticatedApi.post(`${API_BASE_URL}/employees`, payload);
    } catch (error) {
      throw new ApiError('Failed to create employee', error.status || 500);
    }
  },

  updateEmployee: async (id, employeeData) => {
    try {
      const payload = {
        email: employeeData.email,
        fullName: employeeData.fullName,
        hireDate: employeeData.hireDate,
        contractTypeId: employeeData.contractType,
        engagementModelId: employeeData.engagementModel || null,
        contractCompanyId: employeeData.contractCompany,
        workingLocationId: employeeData.workingLocation,
        clusterId: employeeData.cluster,
        gradeId: employeeData.grade,
        positionId: employeeData.position,
        status: employeeData.status
      };

      // Only add resignation details if status is inactive
      if (employeeData.status === 'inactive') {
        payload.resignDate = employeeData.resignDate;
        payload.resignReason = employeeData.resignReason;
        payload.resignReasonDetails = employeeData.resignReasonDetails;
      }

      return await authenticatedApi.put(`${API_BASE_URL}/employees/${id}`, payload);
    } catch (error) {
      // ADR-167 D6: a Region move refused by the open-contract limit (409)
      // carries a sentence the person must see — pass it through.
      if (error.status === 409) {
        throw new ApiError(error.data?.error || 'Failed to update employee', 409, error.data || null);
      }
      throw new ApiError('Failed to update employee', error.status || 500);
    }
  },

  addLeafToEmployee: async (employeeData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/employees/leaves`, employeeData);
    } catch (error) {
      throw new ApiError('Failed to add leaf to employee', error.status || 500);
    }
  },

  removeLeafFromEmployee: async (employeeId, leafSegmentId, competencyLevelId, customSegmentId) => {
    try {
      const payload = {
        ...(leafSegmentId && { leafSegmentId }),
        ...(customSegmentId && { customSegmentId }),
        ...(competencyLevelId && { competencyLevelId })
      };
      return await authenticatedApi.put(`${API_BASE_URL}/employees/${employeeId}/leaves`, payload);
    } catch (error) {
      throw new ApiError('Failed to remove leaf from employee', error.status || 500);
    }
  },

  getEmployeeHierarchy: async (employeeId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employees/${employeeId}/hierarchy`);
    } catch (error) {
      throw new ApiError('Failed to fetch employee hierarchy', error.status || 500);
    }
  },

  getAccessibleProjects: async (employeeId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employees/${employeeId}/accessible-projects`);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError('Access denied', 403);
      }
      if (error.status === 404) {
        throw new ApiError('Employee not found', 404);
      }
      throw new ApiError('Failed to fetch accessible projects', error.status || 500);
    }
  },

  getEmployeeOrganization: async (employeeId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employees/${employeeId}/organization`);
    } catch (error) {
      throw new ApiError('Failed to fetch employee organization', error.status || 500);
    }
  },

  updateEmployeeProjects: async (azureId, projectIds) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/employees/${azureId}/projects`, { projectIds });
    } catch (error) {
      throw new ApiError('Failed to update employee projects', error.status || 500);
    }
  },

  getEmployeeGradeHistory: async (azureId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employees/${azureId}/grade/history`);
    } catch (error) {
      throw new ApiError('Failed to fetch employee grade history', error.status || 500);
    }
  },

  getEmployeePositionHistory: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employees/${id}/position/history`);
    } catch (error) {
      throw new ApiError('Failed to fetch position history', error.status || 500);
    }
  },

  getEmployeeProjectHistory: async (azureId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employees/${azureId}/project/history`);
    } catch (error) {
      throw new ApiError('Failed to fetch employee project history', error.status || 500);
    }
  },

  getEmployeeRoleHistory: async (azureId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employees/${azureId}/role/history`);
    } catch (error) {
      throw new ApiError('Failed to fetch employee role history', error.status || 500);
    }
  },

  //Cached
  getEmployeePhoto,

  getNotifications: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/notifications`);
    } catch (error) {
      throw new ApiError('Failed to fetch notifications', error.status || 500);
    }
  },

  getTeamNotifications: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/notifications/my-team`);
    } catch (error) {
      throw new ApiError('Failed to fetch team notifications', error.status || 500);
    }
  },

  addPositionHistory: async (id, historyData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/employees/${id}/positions`, historyData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid position history data', 400);
      }
      // ADR-169 — overlap / inconsistent-history refusals carry their own message.
      if (error.status === 409) {
        throw new ApiError(error.data?.error || 'This entry overlaps an existing position entry', 409);
      }
      throw new ApiError('Failed to add position history entry', error.status || 500);
    }
  },

  updatePositionHistory: async (employeeId, historyId, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/employees/${employeeId}/position/history/${historyId}`, data);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Position history record not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid position data', 400);
      }
      throw new ApiError('Failed to update position history', error.status || 500);
    }
  },

  deletePositionHistory: async (employeeId, historyId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/employees/${employeeId}/position/history/${historyId}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Position history record not found', 404);
      }
      throw new ApiError('Failed to delete position history', error.status || 500);
    }
  },

  updateGradeHistory: async (employeeId, historyId, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/employees/${employeeId}/grade/history/${historyId}`, data);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Grade history record not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid grade data', 400);
      }
      throw new ApiError('Failed to update grade history', error.status || 500);
    }
  },

  deleteGradeHistory: async (employeeId, historyId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/employees/${employeeId}/grade/history/${historyId}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Grade history record not found', 404);
      }
      throw new ApiError('Failed to delete grade history', error.status || 500);
    }
  },

  addGradeHistory: async (employeeId, data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/employees/${employeeId}/grade/history`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid grade history data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Employee not found', 404);
      }
      throw new ApiError('Failed to add grade history', error.status || 500);
    }
  },

  addRoleHistory: async (id, historyData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/employees/${id}/roles`, historyData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid role history data', 400);
      }
      throw new ApiError('Failed to add role history entry', error.status || 500);
    }
  },

  updateRoleHistory: async (employeeId, historyId, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/employees/${employeeId}/role/history/${historyId}`, data);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Role history record not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid role data', 400);
      }
      throw new ApiError('Failed to update role history', error.status || 500);
    }
  },

  deleteRoleHistory: async (employeeId, historyId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/employees/${employeeId}/role/history/${historyId}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Role history record not found', 404);
      }
      throw new ApiError('Failed to delete role history', error.status || 500);
    }
  },

  endRoleHistory: async (employeeId, roleId, endDate = null) => {
    try {
      const payload = endDate ? { endDate } : {};
      return await authenticatedApi.delete(`${API_BASE_URL}/employees/${employeeId}/roles/${roleId}`, { data: payload });
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Role not found', 404);
      }
      throw new ApiError('Failed to end role', error.status || 500);
    }
  },

  resignEmployee: async (employeeId, resignData) => {
    const response = await authenticatedApi.post(`${API_BASE_URL}/employees/${employeeId}/resign`, resignData);
    return response.data;
  },

  getTechnologyComparison: async (employeeId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employees/${employeeId}/technologies/comparison`);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError('Access denied', 403);
      }
      if (error.status === 404) {
        throw new ApiError('Employee not found', 404);
      }
      throw new ApiError('Failed to fetch technology comparison', error.status || 500);
    }
  },

  addTechnologyToRadar: async (employeeId, { leafSegmentId, competencyLevelId }) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/employees/${employeeId}/technologies/add-to-radar`, {
        leafSegmentId,
        competencyLevelId
      });
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError('Access denied', 403);
      }
      if (error.status === 404) {
        throw new ApiError('Technology not found', 404);
      }
      if (error.status === 409) {
        throw new ApiError('Technology already exists in radar', 409);
      }
      throw new ApiError('Failed to add technology to radar', error.status || 500);
    }
  },

  addTechnologyToCV: async (employeeId, { cvId, leafSegmentId, competencyLevelId }) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/employees/${employeeId}/technologies/add-to-cv`, {
        cvId,
        leafSegmentId,
        competencyLevelId
      });
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError('Access denied', 403);
      }
      if (error.status === 404) {
        throw new ApiError('Technology not found', 404);
      }
      if (error.status === 409) {
        throw new ApiError('Technology already exists in CV', 409);
      }
      throw new ApiError('Failed to add technology to CV', error.status || 500);
    }
  },

  syncTechnologyToCV: async (employeeId, { cvId, leafSegmentId, competencyLevelId }) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/employees/${employeeId}/technologies/sync-to-cv`, {
        cvId,
        leafSegmentId,
        competencyLevelId
      });
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError('Access denied', 403);
      }
      if (error.status === 404) {
        throw new ApiError('Technology not found', 404);
      }
      throw new ApiError('Failed to sync technology to CV', error.status || 500);
    }
  },

  syncTechnologyToRadar: async (employeeId, { leafSegmentId, competencyLevelId }) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/employees/${employeeId}/technologies/sync-to-radar`, {
        leafSegmentId,
        competencyLevelId
      });
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError('Access denied', 403);
      }
      if (error.status === 404) {
        throw new ApiError('Technology not found', 404);
      }
      throw new ApiError('Failed to sync technology to radar', error.status || 500);
    }
  },

  /**
   * Get Manager of Record history for an employee
   * @param {string} employeeId - The employee ID
   * @param {Object} options - Pagination and sorting options
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.limit - Items per page (default: 10)
   * @param {string} options.sortOrder - Sort order: 'asc' or 'desc' (default: 'desc')
   * @returns {Promise<Object>} - Object containing data array and pagination info
   */
  getManagerOfRecordHistory: async (employeeId, options = {}) => {
    try {
      const { page = 1, limit = 10, sortOrder = 'desc' } = options;
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('limit', limit);
      queryParams.append('sortOrder', sortOrder);

      return await authenticatedApi.get(
        `${API_BASE_URL}/employees/${employeeId}/manager-of-record/history?${queryParams.toString()}`
      );
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Employee not found', 404);
      }
      throw new ApiError('Failed to fetch manager of record history', error.status || 500);
    }
  },

  /**
   * Get Manager history for an employee (responsibilities)
   * @param {string} employeeId - The employee ID
   * @returns {Promise<Object>} - Object containing data array and pagination info
   */
  getManagerHistory: async (employeeId) => {
    try {
      return await authenticatedApi.get(
        `${API_BASE_URL}/employees/${employeeId}/manager-history`
      );
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Employee not found', 404);
      }
      throw new ApiError('Failed to fetch manager history', error.status || 500);
    }
  },
};